<script>
import { required } from 'vuelidate/lib/validators';
import { cloneDeep } from 'lodash';
import DatePicker from "vue2-datepicker";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import MaskedInput from "vue-text-mask";
import AccountModal from "./account-modal.vue";
import SLModal from "./sl-modal.vue";

export default {
    name: "JevModal",
    props: {
        modalTitle: String,
        journalType: {
            type: String,
            default: 'General Journal'
        },
        journals: {
            type: Array,
            default: () => []
        },
        fundSources: {
            type: Array,
            default: () => []
        },
        accounts: {
            type: Array,
            default: () => []
        }
    },
    components: {
        DatePicker,
        ResponsibilityCenter: () => import("@/components/modals/responsibility-center.vue"),
        MaskedInput,
        AccountModal,
        SLModal
    },
    validations: {
        form: {
            jevDate: {required},
            responsibilityCenter: {required},
            description: {required},
            fundSourceId: {required}
        }
    },
    data(){
        return {
            submitAcc: false,
            currencyMask: createNumberMask({
                prefix: "",
                allowDecimal: true,
            }),
            form: {
                id: 0,
                jevDate: new Date(),
                jevNumber: null,
                responsibilityCenter: null,
                description: null,
                sourceDocument: null,
                fundSourceId: null,
                journalTypeId: null,
                jevDetails: [],
            },
            newAcc: {
                id: 0,
                accountName: "",
                accountId: 0,
                debit: '',
                credit: '',
                accountSearch: '',
                searched: false,
                failedSearch: false,
                subsidiaryLedgerId: 0,
                slCode: '',
            },
            submitted: false,
            loadJEVNumber: false,
        }
    },
    computed: {
        totalDebit() {
            return this.form.jevDetails.reduce((sum, item) => sum + parseFloat(item.debit || 0), 0);
        },
        totalCredit() {
            return this.form.jevDetails.reduce((sum, item) => sum + parseFloat(item.credit || 0), 0);
        },
        fundSourceOptions() {
            return [
                { value: 0, text: 'Select Fund Source...', disabled: true },
                ...this.fundSources.map(fs => ({ value: fs.id, text: fs.sourceName }))
            ];
        },
        accountOptions() {
            return [
                { value: 0, text: 'Select Account...', disabled: true },
                ...this.accounts.map(acc => ({ value: acc.id, text: `${acc.code} - ${acc.name}` }))
            ];
        },
        isEditingAccount(){
            return this.form.jevDetails.some(item => item.isEdit);
        }
    },
    methods: {
        updateData(data){
            this.form = cloneDeep(data);
            this.submitted = false;
        },
        updateDetails(data){
            this.form.jevDetails = cloneDeep(data);
        },
        resetForm(){
            this.form = {
                id: 0,
                jevDate: new Date(),
                jevNumber: null,
                responsibilityCenter: null,
                description: null,
                sourceDocument: null,
                fundSourceId: null,
                journalTypeId: this.journals.length > 0 ? this.journals[0].id : null,
                jevDetails: [],
            }
            this.newAcc = {
                id: 0,
                accountName: "",
                accountId: 0,
                debit: '',
                credit: '',
                accountSearch: '',
                subsidiaryLedgerId: 0,
                isSundry: false,
                searched: false,
                failedSearch: false,
                slCode: '',
            }
            this.submitAcc = false;
            this.submitted = false;
            this.$v.$reset();
        },
        async generateJevNumber(){
            this.loadJEVNumber = true;
            await this.$store
                .dispatch("jev/generateJevNumber", { cycle: this.$store.state.data.cycle, campusId: this.$store.state.data.selectedCampus.id })
                .then((res) => {
                    if (res.data.error) {
                        this.form.jevNumber = '';
                    }
                    this.form.jevNumber = res.data;
                })
                .catch((err) => {
                    this.form.jevNumber = '';
                });
            this.loadJEVNumber = false;
        },
        onCreate(createAndPost = false){
            this.submitted = true;
            this.$v.form.$touch();
            if(this.$v.form.$invalid) return;
            if(this.form.jevDetails.length === 0) {
                this.$swal({
                    icon: 'error',
                    title: 'No Journal Details',
                    text: 'Please add at least one journal entry detail'
                });
                return;
            }
            let data = cloneDeep(this.form);
            data.createAndPost = createAndPost;
            data.jevDate = this.getDate(this.form.jevDate);
            data.jevDetails = data.jevDetails.map(item => {
                return {
                    ...item,
                    debit: this.getExactAmt(item.debit) || 0,
                    credit: this.getExactAmt(item.credit) || 0,
                    isSundry: item.isSundry || false
                };
            });
            this.$emit('submit', data);
        },
        removeJevDetail(index) {
            this.form.jevDetails.splice(index, 1);
        },
        onAccountChange(index, accountId) {
            const account = this.accounts.find(acc => acc.id === accountId);
            if (account) {
                this.$set(this.form.jevDetails, index, {
                    ...this.form.jevDetails[index],
                    accountId: accountId,
                    accountName: `${account.code} - ${account.name}`
                });
            }
        },
        searchResponsibilityCenter() {
            this.$bvModal.show("responsibilityCenterModal");
        },
        selectResponsibilityCenter(data) {
            this.$swal({
                title: "Select code to use",
                html: `<p><span class="text-primary">${data.rcCodeGf} </span> - GF Code</p><p><span class="text-success">${data.rcCodeStf}</span> - STF Code</p>`,
                icon: "info",
                showCancelButton: true,
                confirmButtonColor: "#564ab1",
                confirmButtonText: "GF Code",
                cancelButtonColor: "#28c76f",
                cancelButtonText: "STF Code",
            }).then((result) => {
                if (result.isConfirmed) {
                    this.form.responsibilityCenter = data.rcCodeGf;
                    this.$bvModal.hide("responsibilityCenterModal");
                } else {
                    this.form.responsibilityCenter = data.rcCodeStf;
                    this.$bvModal.hide("responsibilityCenterModal");
                }
            });
        },
        getTotalDebit() {
            return this.form.jevDetails.reduce((total, item) => {
                return total + parseFloat(item.debit || 0);
            }, 0);
        },
        getTotalCredit() {
            return this.form.jevDetails.reduce((total, item) => {
                return total + parseFloat(item.credit || 0);
            }, 0);
        },
        searchAccount(item){
            if(item){
                this.selectedItem = item;
            }else{
                this.selectedItem = null;
            }
            this.$bvModal.show("search-account-modal");
        },
        resetItem() {
            this.newAcc = {
                id: 0,
                accountName: "",
                accountId: 0,
                debit: '',
                credit: '',
                isSundry: false
            };
        },
        selectAccount(account) {
            if(this.selectedItem){
                this.selectedItem.accountId = account.id;
                this.selectedItem.accountName = account.accountName;
                this.selectedItem.uacs = account.uacs;
            }else{
                this.newAcc.accountId = account.id;
                this.newAcc.accountName = account.accountName;
                this.newAcc.uacs = account.uacs;
                this.newAcc.searched = true;
                this.newAcc.failedSearch = false;
            }
        },
        findAccount(){
            let searchAcc = this.accounts.find(item => item.uacs.replace(/ /g,'') === this.newAcc.accountSearch.replace(/ /g,''));
            if(searchAcc){
                this.newAcc.accountId = searchAcc.id;
                this.newAcc.accountName = searchAcc.accountName;
                this.newAcc.uacs = searchAcc.uacs;
                this.newAcc.searched = true;
                this.newAcc.failedSearch = false;
                this.$refs.accDebitAdd.$el.focus();
            }else{
                this.newAcc.accountId = null;
                this.newAcc.accountName = '';
                this.newAcc.uacs = '';
                this.newAcc.searched = false;
                this.newAcc.failedSearch = true;
            }
        },
        async addToJev(){
            if(this.newAcc.accountId == 0 || this.newAcc.debit === '' && this.newAcc.credit === '') return this.submitAcc = true;
            let dat = cloneDeep(this.newAcc);
            dat.debit = this.getExactAmt(this.newAcc.debit);
            dat.credit = this.getExactAmt(this.newAcc.credit);
            dat.isEdit = false;
            this.form.jevDetails.push(dat);
            this.resetItem();
            await this.wait(100);
            this.$refs.accountSearch.focus();
            this.submitAcc = false;
        },
        editItem(item) {
            item.isEdit = true;
            this.copyAcc = cloneDeep(item);
        },
        editToJev(item, index){
            if(item.accountId == 0 || item.debit === '' && item.credit === '') return this.submitAcc = true;
            let dat = cloneDeep(item);
            dat.debit = this.getExactAmt(item.debit);
            dat.credit = this.getExactAmt(item.credit);
            dat.isEdit = false;
            this.form.jevDetails.splice(index, 1, dat);
            this.$refs.accountSearch.focus();
            this.submitAcc = false;
        },
        resetUpdateItem(index) {
            this.form.jevDetails.splice(index, 1, {...this.copyAcc, isEdit: false});
        },
        deleteItem(index) {
            this.form.jevDetails.splice(index, 1);
        },
        addSL(type, item = null){
            this.selectedItem = item;
            this.slTagType = type;
            this.$bvModal.show("sl-modal");
        },
        selectSL(data){
            if(this.slTagType === 'new'){
                this.newAcc.subsidiaryLedgerId = data.id;
                this.newAcc.slCode = data.code;
                this.newAcc.subsidiaryLedger = data;
            }else{
                this.selectedItem.subsidiaryLedgerId = data.id;
                this.selectedItem.slCode = data.code;
                this.selectedItem.subsidiaryLedger = data;
            }
        },
        removeSL(item){
            item.subsidiaryLedgerId = 0;
            item.slCode = '';
        },
    }
}
</script>

<template>
    <b-modal
        id="jev-modal"
        :title="modalTitle"
        size="xl"
        title-class="font-18"
        hide-footer
        no-close-on-backdrop
    >
        <form
            @submit.prevent="onCreate(false)"
            class="needs-validation"
            ref="fcForm"
        >
            <!-- Main Form Fields -->
            <div class="d-flex gap-1 mb-3">
                <div class="border px-3 py-2 text-center rounded cursor-pointer w-100 d-flex align-items-center justify-content-center" v-for="x in journals" :key="x.id" :class="{'bg-purple text-white': form.journalTypeId === x.id}" @click="form.journalTypeId = x.id">
                    {{x.journalTypeName}}
                </div>
            </div>
            <div class="row">
                <!-- Left Column -->
                <div class="col-md-4">
                    <div class="mb-3">
                        <label for="jevDate">JEV/DV/RCD Number </label>
                        <template v-if="loadJEVNumber">
                            <div class="d-flex justify-content-center align-items-center" style="height:38px;">
                                <div class="spinner-border text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <input type="text" class="form-control" v-model="form.jevNumber"  />
                        </template>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="mb-3">
                        <label for="jevDate">Journal Entry Date </label>
                        <date-picker
                            id="jevDate"
                            v-model="form.jevDate"
                            :first-day-of-week="1"
                            lang="en"
                            placeholder="Enter Journal Entry Date..."
                            format="MMMM DD, YYYY"
                            :class="{
                                'is-invalid': submitted && $v.form.jevDate.$error,
                            }"
                        />
                        <div
                            v-if="submitted && $v.form.jevDate.$error"
                            class="invalid-feedback"
                        >
                            <span v-if="!$v.form.jevDate.required">This value is required.</span>
                        </div>
                    </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-4">
                    <div class="mb-3">
                        <b-form-group
                            class="mb-3"
                            label="Responsibility Center"
                            label-for="responsibilityCenter"
                        >
                            <b-input-group>
                                <template #append>
                                    <b-button
                                        v-if="!form.responsibilityCenter"
                                        variant="outline-info"
                                        @click="searchResponsibilityCenter"
                                        ><i class="mdi mdi-clipboard-text-search-outline"></i
                                    ></b-button>
                                    <b-button
                                        v-else
                                        @click="form.responsibilityCenter = null"
                                        variant="outline-danger"
                                        title="Remove Responsibility Center Code"
                                        ><i class="mdi mdi-close-thick"></i
                                    ></b-button>
                                </template>
                                <b-form-input
                                    id="responsibilityCenter"
                                    v-model="form.responsibilityCenter"
                                    type="text"
                                    placeholder="Enter Responsibility Center Code..."
                                    autocomplete="off"
                                    :class="{
                                        'is-invalid':
                                        submitted && $v.form.responsibilityCenter.$error,
                                    }"
                                >
                                </b-form-input>
                            </b-input-group>
                        </b-form-group>
                        <div
                            v-if="submitted && $v.form.responsibilityCenter.$error"
                            class="invalid-feedback"
                        >
                            <span v-if="!$v.form.responsibilityCenter.required">This value is required.</span>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-6">
                    <div class="mb-3">
                        <label for="fundSource">Fund Source </label>
                        <b-form-select
                            class="form-select"
                            id="fundSource"
                            v-model="form.fundSourceId"
                            :options="fundSourceOptions"
                            autocomplete="off"
                            :class="{
                                'is-invalid': submitted && $v.form.fundSourceId.$error,
                            }"
                        />
                        <div
                            v-if="submitted && $v.form.fundSourceId.$error"
                            class="invalid-feedback"
                        >
                            <span v-if="!$v.form.fundSourceId.required">This value is required.</span>
                        </div>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="mb-3">
                        <label for="sourceDocument">Source Document</label>
                        <b-form-input
                            id="sourceDocument"
                            v-model="form.sourceDocument"
                            placeholder="Enter Source Document..."
                            autocomplete="off"
                        />
                    </div>
                </div>
            </div>
            <div class="mb-3">
                <label for="description">Description </label>
                <b-form-textarea
                    id="description"
                    v-model="form.description"
                    placeholder="Enter Description..."
                    rows="3"
                    autocomplete="off"
                    :class="{
                        'is-invalid': submitted && $v.form.description.$error,
                    }"
                />
                <div
                    v-if="submitted && $v.form.description.$error"
                    class="invalid-feedback"
                >
                    <span v-if="!$v.form.description.required">This value is required.</span>
                </div>
            </div>
            <!-- Journal Entry Details Section -->
            <hr />
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Journal Entry Details</h5>
            </div>
            <table class="table table-sm align-middle table-bordered border-primary mb-0">
                <thead>
                    <tr>
                        <th >UACS</th>
                        <th>Account Name</th>
                        <th class="text-center"  style="width:170px;">Debit</th>
                        <th class="text-center"  style="width:170px;">Credit</th>
                        <th class="text-center"  style="width:90px;">SL</th>
                        <!-- <th class="text-center"  style="width:90px;">Sundry</th> -->
                        <th class="text-center" style="width:90px;">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(item, index) in form.jevDetails"
                        :key="index"
                    >
                        <td class="col-md-2" scope="row">
                            <template v-if="!item.isEdit">{{ item.uacs }}</template>
                            <template v-else>
                                <b-form-input
                                    v-model="item.uacs"
                                    placeholder="Enter UACS"
                                ></b-form-input>
                            </template>
                        </td>
                        <td class="col-md-4">
                            <template v-if="!item.isEdit">{{ item.accountName }}</template>
                            <template v-else>
                                <b-input-group>
                                    <template #append>
                                        <b-button
                                            type="button"
                                            @click="searchAccount(item)"
                                            variant="outline-info"
                                            ><i class="mdi mdi-clipboard-text-search-outline"></i
                                        ></b-button>
                                    </template>
                                    <b-form-input
                                        v-model="item.accountName"
                                        placeholder="Enter Account Name"
                                    ></b-form-input>
                                </b-input-group>
                            </template>
                        </td>
                        <td class="text-center">
                            <template v-if="!item.isEdit">{{item.debit ? formatCurrency(item.debit) : '-'}}</template>
                            <template v-else>
                                <form @submit.prevent="editToJev(item, index)">
                                    <masked-input
                                        ref="accDebit"
                                        autocomplete="off"
                                        type="text"
                                        v-model="item.debit"
                                        class="form-control me-1 text-center form-control-sm"
                                        :mask="currencyMask"
                                        :class="{'is-invalid': submitAcc && !item.debit}"
                                    >
                                    </masked-input>
                                </form>
                            </template>
                        </td>
                        <td class="text-center">
                            <template v-if="!item.isEdit">{{item.credit ? formatCurrency(item.credit) : '-'}}</template>
                            <template v-else>
                                <form @submit.prevent="editToJev(item, index)">
                                    <masked-input
                                        autocomplete="off"
                                        type="text"
                                        v-model="item.credit"
                                        class="form-control me-1 text-center form-control-sm"
                                        :mask="currencyMask"
                                        :class="{'is-invalid': submitAcc && !item.credit}"
                                    >
                                    </masked-input>
                                </form>
                            </template>
                        </td>
                        <!-- <td class="text-center">
                            <div class="form-check form-checkbox-outline form-check-success mb-0 d-flex justify-content-center align-items-center">
                                <input
                                    type="checkbox"
                                    class="form-check-input cb-large position-relative"
                                    v-model="item.isSundry"
                                    :disabled="!item.isEdit"
                                />
                            </div>
                        </td> -->
                        <td class="text-center cursor-pointer" id="for subsidiaryLedgerId" @click="addSL('old', item)">
                            <template v-if="!item.subsidiaryLedgerId">
                                <a href="javascript:void(0);">Tag SL</a>
                            </template>
                            <template v-else>
                                <a href="javascript:void(0);" class="font-size-14 mb-0" :id="`A-${index}`">{{ item.slCode }}</a>
                                <b-popover :target="`A-${index}`" placement="left" triggers="hover focus">
                                    <template #title>Subsidiary Ledger</template>
                                    <div class="text-truncate">{{ item.slCode }}</div>
                                    <div class="font-size-13 text-success font-weight-bold">{{ item.subsidiaryLedger.clientDto.name }}</div>
                                    <div>{{item.subsidiaryLedger.slType }}</div>
                                </b-popover>
                                <a href="javascript:void(0);" @click.stop="removeSL(item)" class="text-danger ms-1"><i class="bx bx-x"></i></a>
                            </template>
                        </td>
                        <td class="text-center col-md-1">
                            <template v-if="!item.isEdit">
                                <a href="javascript:void(0)" @click="editItem(item)" v-if="!isEditingAccount">
                                    <i class="bx bx-edit text-success font-size-22 "></i>
                                </a>
                                <a href="javascript:void(0)" @click="deleteItem(index)" v-if="!isEditingAccount">
                                    <i class="bx bx-trash text-danger font-size-22 "></i>
                                </a>
                            </template>
                            <template v-if="item.isEdit">
                                <a href="javascript:void(0)" @click="editToJev(item, index)" >
                                    <i class="bx bx-check me-2 text-green font-size-22 "></i>
                                </a>
                                <a href="javascript:void(0)" @click="resetUpdateItem(index)" >
                                    <i class="bx bx-x text-danger font-size-22 "></i>
                                </a>
                            </template>
                        </td>
                    </tr>
                    <tr>
                        <template v-if="!newAcc.accountId">
                            <td colspan="2">
                                <form @submit.prevent="findAccount">
                                    <b-input-group>
                                        <template #append>
                                        <b-button
                                            type="button"
                                            @click="searchAccount(null)"
                                            variant="outline-info"
                                            :disabled="isEditingAccount"
                                            ><i class="mdi mdi-clipboard-text-search-outline"></i
                                        ></b-button>
                                        </template>
                                        <input 
                                            ref="accountSearch"
                                            autocomplete="off"
                                            type="text" 
                                            class="form-control" 
                                            placeholder="Type the UACS or Search Account" 
                                            v-model="newAcc.accountSearch"
                                            :disabled="isEditingAccount"
                                            :class="{'is-invalid': newAcc.accountSearch && newAcc.failedSearch}"
                                        >
                                    </b-input-group>
                                    <span v-if="newAcc.accountSearch && newAcc.failedSearch">
                                        <small class="text-danger" v-if="newAcc.failedSearch">Account not found</small>
                                    </span>
                                </form>
                            </td>
                        </template>
                        <template v-else>
                            <td>{{newAcc.uacs}}</td>
                            <td>{{newAcc.accountName}}</td>
                        </template>
                        <td>
                            <form @submit.prevent="addToJev">
                                <masked-input
                                    ref="accDebitAdd"
                                    autocomplete="off"
                                    type="text"
                                    v-model="newAcc.debit"
                                    :disabled="isEditingAccount"
                                    class="form-control me-1 text-center form-control-sm"
                                    :mask="currencyMask"
                                    :class="{'is-invalid': submitAcc && !newAcc.debit}"
                                >
                                </masked-input>
                            </form>
                        </td>
                        <td>
                            <form @submit.prevent="addToJev">
                                <masked-input
                                    autocomplete="off"
                                    type="text"
                                    v-model="newAcc.credit"
                                    class="form-control me-1 text-center form-control-sm"
                                    :mask="currencyMask"
                                    :disabled="isEditingAccount"
                                    :class="{'is-invalid': submitAcc && !newAcc.credit}"
                                >
                                </masked-input>
                            </form>
                        </td>
                        <!-- <td class="text-center" @click="newAcc.isSundry = !newAcc.isSundry">
                            <div class="form-check form-checkbox-outline form-check-success mb-0 d-flex justify-content-center align-items-center">
                                <input
                                    type="checkbox"
                                    class="form-check-input cb-large position-relative"
                                    v-model="newAcc.isSundry"
                                />
                            </div>
                        </td> -->
                        <td class="text-center" id="for subsidiaryLedgerId" @click="addSL('new')">
                            <template v-if="!newAcc.subsidiaryLedgerId && newAcc.accountId && (newAcc.debit !== '' || newAcc.credit !== '')">
                                <a href="javascript:void(0);">Tag SL</a>
                            </template>
                            <template v-if="newAcc.subsidiaryLedgerId">
                                <span class="badge bg-info font-size-12">{{ newAcc.slCode }}</span>
                                <a href="javascript:void(0);" @click="removeSL(newAcc)">Remove</a>
                            </template>
                        </td>
                        <td class="text-center">
                            <template v-if="newAcc.accountId != 0 || newAcc.debit != '' || newAcc.credit != ''">
                                <a href="javascript:void(0)" @click="addToJev" >
                                    <i class="bx bx-check me-2 text-green font-size-24 "></i>
                                </a>
                                <a href="javascript:void(0)" @click="resetItem" >
                                    <i class="bx bx-x text-danger font-size-24 "></i>
                                </a>
                            </template>
                        </td>
                    </tr>
                    <tr>
                        <td colspan="2" class="text-right">Total:</td>
                        <td class="text-center">{{ formatCurrency(getTotalDebit()) }}</td>
                        <td class="text-center">{{ formatCurrency(getTotalCredit()) }}</td>
                        <td colspan="2"></td>
                    </tr>
                </tbody>
            </table>

            <!-- Modal Footer -->
            <hr />
            <div class="text-end">
                <b-dropdown
                    id="dropdown-dropleft"
                    right
                    variant="link"
                    toggle-class="text-decoration-none"
                    menu-class="dropdown-menu-end"
                    no-caret
                    v-if="form.id === 0"
                >
                    <template #button-content>
                        <button type="button" class="btn btn-primary" :disabled="form.jevDetails.length === 0">
                            <i class="bx bx-save me-1"></i>
                            Save
                        </button>
                    </template>
                    <b-dropdown-item
                        @click="onCreate(false)"
                        ><i class="bx bx-receipt me-1"></i> Save As Draft</b-dropdown-item
                    >
                    <b-dropdown-item
                        @click="onCreate(true)"
                        ><i class="bx bx-check me-1"></i> Save &amp; Post</b-dropdown-item
                    >
                </b-dropdown>
                 <button type="button" class="btn btn-primary" @click="onCreate(true)" v-else>
                    <i class="bx bx-save me-1"></i>
                    Update
                </button>
                <button
                    type="button"
                    class="btn btn-link ms-2"
                    @click="$bvModal.hide('jev-modal')"
                >
                    Cancel
                </button>
            </div>
        </form>
        <ResponsibilityCenter
            ref="responsibilityCenter"
            :campus="$store.state.data.selectedCampus.id"
            @selectRow="selectResponsibilityCenter"
        />
        <AccountModal 
            :accounts="accounts"
            @select-account="selectAccount"
        />
        <SLModal 
            @selectSubsidiaryLedger="selectSL"
        />
    </b-modal>
</template>

<style scoped>
.table th, .table td {
    vertical-align: middle;
}

.font-size-48 {
    font-size: 48px;
}

.is-invalid .mx-datepicker {
    border: 1px solid #f46a6a;
}
</style>